/** @format */

import {EventComponentBus} from '@/js/events/event-componentbus.js';
import {defaultColours, defaultPalette} from './../baseCssTheme';
import parser from 'ua-parser-js';
import StackTrace from 'stacktrace-js';
import $cs_ from '@/js/plugins/cs-underscore-helpers';
import $csNetworkCall from '@/js/modules/csNetworkCall.js';
import $csGridExport from '@/js/modules/csGridExport'; //ES6 module that handles export from grid
import chroma from 'chroma-js';

let logoutInProgress = false;

export default {
	install: function(Vue) {
		//register the $cs_ 'cs-underscore-helper' library methods on the Vue instance
		Object.defineProperty(Vue.prototype, '$cs_', {value: $cs_});

		//register the $csNetworkCall library methods on the Vue instance
		Object.defineProperty(Vue.prototype, '$csNetworkCall', {value: $csNetworkCall});

		/**
		 * @public
		 * @global
		 * @description
		 * Returns app multilingual parameter as boolean from a config file
		 *
		 * @returns { Boolean }
		 */
		Vue.prototype.$isMultiLingual = function() {
			if (!this.$root.$data.config) {
				return false;
			}

			let isMultiLingual = this.$root.$data.config.isMultiLingual;

			return typeof isMultiLingual === 'boolean' ? isMultiLingual : isMultiLingual == 'true';
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns whetever a device is a mobile device
		 * Method provided by: https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
		 *
		 * @returns { Boolean }
		 */
		Vue.prototype.$isMobile = function() {
			let hasTouchScreen = false;

			if ('maxTouchPoints' in navigator) {
				hasTouchScreen = navigator.maxTouchPoints > 0;
			} else if ('msMaxTouchPoints' in navigator) {
				hasTouchScreen = navigator.msMaxTouchPoints > 0;
			} else {
				let mQ = window.matchMedia && matchMedia('(pointer:coarse)');

				if (mQ && mQ.media === '(pointer:coarse)') {
					hasTouchScreen = !!mQ.matches;
				} else if ('orientation' in window) {
					hasTouchScreen = true; // deprecated, but good fallback
				} else {
					// Only as a last resort, fall back to user agent sniffing
					let UA = navigator.userAgent;

					hasTouchScreen = /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
				}
			}

			return hasTouchScreen;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns an app accessibility mode parameter
		 *
		 * @returns { Boolean } accessibility mode value
		 */
		Vue.prototype.$accessibilityMode = function() {
			return this.$root.$data.userSettings && typeof this.$root.$data.userSettings.accessibilityMode != 'undefined' && this.$root.$data.userSettings.accessibilityMode !== null
				? this.$root.$data.userSettings.accessibilityMode
				: this.$root.$data.platformInstanceSettings.accessibilityMode;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Adds error data to the array and call CsSentryPlugin method
		 *
		 * @returns void
		 */
		Vue.prototype.$recordError = function(error, callback) {
			if (!this.$root.$data.errors) return;
			if (process.env.NODE_ENV === 'development') {
				console.error($cs_.deepClone(error));
				console.error(error?.error);
			}
				
			const duplicateCheck = this.$root.$data.errors.find((err) => err.errorCode === error.errorCode);
			if (duplicateCheck == undefined) {
				//in situations where we are programatically setting the error messages,
				// we may well handle the translations in the code itself. We can send these with `translatedErrorMessage`
				if (error.translatedErrorMessage) {
					error.errorMessage = error.translatedErrorMessage;
				} else {
					// for dynamic error strings we can pass an array of translationArgs which will populate the
					// translation placeholder
					if (error.translationArgs) {
						error.errorMessage = this.$translateWithArgs(`errors.${error.errorCode}`, error.translationArgs);
					} else {
						error.errorMessage = this.$translateWithDefault(`errors.${error.errorCode}`, error.errorMessage);
					}
				}

				this.$root.$data.errors.push(error);
			}
			if (typeof error === 'object') {
				let fullError = {...error, ...{propsData: this.$options.propsData, componentName: this.$options._componentTag}},
					parsedUA = parser.UAParser(window.navigator.userAgent);

				//normalize to error object type
				if (fullError.error instanceof Error === false) {
					if (typeof fullError.error === 'object') {
						let normalisedError = new Error();
						fullError.error = $cs_.deepAssign(normalisedError, fullError.error);
					} else {
						fullError.error = new Error(fullError.error);
					}
				}

				if (fullError && parsedUA) {
					//async stack trace block code for too long - use sync trace
					let result = StackTrace.getSync();

					if (Array.isArray(result) && result.length > 0) {
						let firstFrame = result[0],
							payload = {
								fields: [
									{
										id: 'Title',
										value: fullError.errorMessage
									},
									{
										id: 'Status',
										value: ['New']
									},
									{
										id: 'ErrorCode',
										value: fullError.errorCode
									},
									{
										id: 'ErrorMessage',
										value: fullError.error.message
									},
									{
										id: 'FullError',
										value: fullError.error.stack
									},
									{
										id: 'LineNumber',
										value: firstFrame.lineNumber
									},
									{
										id: 'FileName',
										value: firstFrame.fileName.replace('webpack:///', '')
									},
									{
										id: 'Release',
										value: 'Recording release numbers?'
									},
									{
										id: 'Url',
										value: window.location.pathname
									},
									{
										id: 'Browser',
										value: parsedUA.browser.name
									},
									{
										id: 'BrowserVersion',
										value: parsedUA.browser.version
									},
									{
										id: 'OS',
										value: parsedUA.os.name
									},
									{
										id: 'OSVersion',
										value: parsedUA.os.version
									},
									{
										id: 'ActionsBreadCrumb',
										value: error.actionsBreadcrumb
									},
									{
										id: 'VisibleToUser',
										value: fullError.displayToUser
									}
								]
							};
						if (fullError.displayToUser === true || fullError.forceRecord === true) {
							this.$axios.post(`${this.$getApiBaseUrl()}/api/FELogging/Log`, payload)
								.then(() => {
									// successfully recorded
								})
								.catch((error) => {
									//console.log(error);
								});
						}
					}
				}
			}
			if (typeof callback == 'function') this.$root.$data.errorsCallback = callback;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns static part of an API url
		 *
		 * @returns { String }
		 */
		Vue.prototype.$getApiBaseUrl = function() {
			if (!this.$root.$data.config) return;

			return `${this.$root.$data.config.apiProtocol}://${this.$root.$data.config.apiDomain}${this.$root.$data.config.apiVersion}`;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns the domain of the web api to which this client connects
		 *
		 * @returns { String }
		 */
		Vue.prototype.$getApiDomain = function() {
			if (!this.$root.$data.config) return;

			return this.$root.$data.config.apiDomain;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns indendity base url
		 *
		 * @returns { String }
		 */
		Vue.prototype.$getApiIdentityBaseUrl = function() {
			if (!this.$root.$data.config) return;

			return `${this.$root.$data.config.identityBase}`;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns app standard page error message from config file
		 *
		 * @returns { String }
		 */
		Vue.prototype.$getStandardPageErrorMessage = function() {
			return this.$root.$data.config.standardPageErrorMessage;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns an app config
		 *
		 * @returns { Object }
		 */
		Vue.prototype.$getRootConfig = function() {
			return this.$root.$data.config;
		};

		/**
		 * @public
		 * @description
		 * Setup color contrast
		 * Utility function to determine the correct contrasting foreground
		 * colour for a given background colour
		 *
		 * @param {String} baseColour The base colour to determine contrast for
		 * @param {String} bodyTextColour The body text colour to default to if the button is light and the body text is dark, or vice versa
		 * @param {String} darkColour The dark colour value to test
		 * @param {String} lightColour The dark colour value to test
		 *
		 * @returns {String}
		 */
		Vue.prototype.$getContrastColour = function(baseColour, bodyTextColour, darkColour, lightColour) {
			let colour, bodyColour;

			if (typeof baseColour === 'string' && baseColour.trim().length > 0) {
				colour = chroma(baseColour.trim());
			} else {
				colour = chroma('#000000');
			}

			if (typeof bodyTextColour === 'string' && bodyTextColour.trim().length > 0) {
				bodyColour = chroma(bodyTextColour.trim());
			} else {
				bodyColour = chroma('#ffffff');
			}

			// If colour is light
			if (colour.get('lab.l') > 75) {
				if (bodyColour.get('lab.l') > 70) {
					return typeof darkColour === 'string' ? darkColour.trim() : '';
				} else {
					return bodyColour.hex();
				}
			}

			// If colour is dark
			// If body colour is light returns body text colour otherwise brighten colour
			if (bodyColour.get('lab.l') > 70) {
				return bodyColour.hex();
			} else {
				return typeof lightColour === 'string' ? lightColour.trim() : '';
			}
		};

		/**
		 * @public
		 * @description
		 * Utility function to darken a given colour by a given percentage amount
		 *
		 * @param {String} baseColour The base colour to change
		 * @param {Number} percentage The percentage amount to darken by
		 *
		 * @returns {String}
		 */
		Vue.prototype.$darkenColour = function(baseColour, percentage) {
			if (typeof baseColour === 'undefined' || (typeof baseColour === 'string' && baseColour.trim().length === 0)) {
				return '';
			}
			return chroma(baseColour.trim())
				.darken(percentage / 100)
				.hex();
		};

		/**
		 * @public
		 * @description
		 * Utility function to darken a given colour by a given percentage amount
		 *
		 * @param {String} baseColour The base colour to change
		 * @param {Number} percentage The percentage amount to lighten by
		 *
		 * @returns {String}
		 */
		Vue.prototype.$lightenColour = function(baseColour, percentage) {
			if (typeof baseColour === 'undefined' || (typeof baseColour === 'string' && baseColour.trim().length === 0)) {
				return;
			}
			return chroma(baseColour.trim())
				.brighten(percentage / 100)
				.hex();
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns an entity theme
		 * @returns { Object }
		 */
		Vue.prototype.$getTheme = function() {
			try {
				const themeSettings = this.$root.activeEntity.themeTemplate.themeSettings;
				return themeSettings;
			} catch (error) {
				return {};
			}
		};

		/**
		 * @public
		 * @description
		 * Returns the current list of colours and the base colour
		 * from either the defaults or a theme. For use in reports
		 * and other places
		 *
		 * @returns {Object}
		 */
		Vue.prototype.$getReportColours = function() {
			const colours = [];
			let colourCount = 0; // we change behaviour based on this

			const theme = this.$getTheme();
			if (theme && theme.colors && theme.colors.report) {
				const themeReportColours = theme.colors.report;

				for (let colourKey in themeReportColours) {
					let reportColour = themeReportColours[colourKey];
					// validate the colours, check they exist and are in hex format
					if (reportColour && reportColour.length > 0 && reportColour.search(/^(#[0-9a-fA-F]{3})([0-9a-fA-F]{3})?$/) >= 0) {
						colourCount++;
						colours.push(reportColour);
					}
				}
			}

			if (colourCount > 0) {
				return {
					colourCount: colourCount,
					colours
				};
			}

			// use theme colours if available
			try {
				return {
					colourCount: 1,
					colours: [theme.colors.primary]
				};
			} catch (e) {
				// fallback in case we have nothing to return
				return {
					colourCount: 2,
					colours: ['#2b303a', '#f9b234']
				};
			}
		};

		/**
		 * @public
		 * @description
		 * Returns the values assigned in our sass file for default theme colours
		 *
		 * @returns {Object}
		 */
		Vue.prototype.$themeDefaultColours = function() {
			return defaultColours;
		};

		/**
		 * @public
		 * @description
		 * Returns the current list of colours and the base colour
		 * from either the defaults or a theme. For use in reports
		 * and other places
		 *
		 * @returns {Object}
		 */
		Vue.prototype.$getColourList = function() {
			// let colourList = {
			//     baseColor : '#2b303a',
			//     list : [
			//         '#2b303a',
			//         '#38545f',
			//         '#517a7c',
			//         '#7aa18c',
			//         '#b3c683',
			//         '#fee93c',
			//         '#ffda3c',
			//         '#ffcc3b',
			//         '#ffbd3a',
			//         '#ffae39',
			//         '#faa100'
			//     ]
			// };

			let colourList = {
				baseColor: '#2b303a',
				list: ['#2a2f39', '#565b66', '#868b97', '#b9bfcc', '#f9b234', '#c17e0f', '#8b4d00', '#572000'],
				reportDefaults: {0: '#000000', 1: '#dddddd'}
			};

			const theme = this.$getTheme();

			if (theme && theme.colors) {
				colourList.baseColor = theme.colors.primary ? theme.colors.primary : theme.colors[Object.keys(theme.colors)[0]];
				colourList.list = [];
				for (let colourKey in theme.colors) {
					let colourValue = theme.colors[colourKey];
					colourList.list.push(colourValue);
				}
			}

			return colourList;
		};

		/**
		 * @public
		 * @description
		 * gets the theme colours with the palette colours unpacked from their nested structure
		 * used in cs-diagram
		 * must not mutate the $getTheme().colors object
		 * @returns 
		 */
		Vue.prototype.$getColourListWithThemePalette = function() {
			let themeColours = this.$getAppColours();
			
			if (themeColours != undefined && themeColours.palette != undefined) {
					for (let {name, color} of themeColours.palette) {
						themeColours[name] = color;
					}
					delete themeColours.palette;
				return themeColours;
			}
			return $cs_.deepClone(defaultPalette);
		};

		/**
		 * @public
		 * @description
		 * returns a palette with compounds suffixes for use in cs-grid export only
		 * must not mutate our theme.colors object
		 * @returns {Object} 
		 */
		Vue.prototype.$getColourListWithPaletteClassSuffixAppends = function() {
			let themeColours = this.$getAppColours();
			if (themeColours && Array.isArray(themeColours.palette)) {
				for (let {classSuffix, name, color} of themeColours.palette) {
					if (classSuffix) themeColours[classSuffix] = color;
					else themeColours[name] = color;
				}
			}
			return Object.assign({}, this.$themeDefaultColours(), themeColours);
		};

		/**
		 * @public
		 * @description
		 * Return an app theme colours
		 * If an app theme is not available it returns app vanilla colours
		 *
		 * @return { Object }
		 */
		Vue.prototype.$getAppColours = function() {
			const vanillaColours = {
				accent: '#FAA100',
				primary: '#2B303A',
				secondary: '#455A64',
				secondarylight: '#607D8B',
				superlight: '#e2e2e2',
				tertiary: '#FFFFFF',
				component: '#FFFFFF',
				dark: '#4A4A4A'
			};

			const theme = this.$getTheme();
			
			let appColours;
			if (theme.colors != undefined) {
				//ensure all vanilla colours are included in any given theme
				appColours = $cs_.deepAssign({}, vanillaColours, theme.colors);
			} else {
				appColours = $cs_.deepClone(vanillaColours);
			}

			//replace any unconfigured values from the vanilla ones if needed
			for (let color in appColours) {
				if (appColours[color] == undefined && vanillaColours[color] != undefined) {
					appColours[color] = vanillaColours[color];
				}
			}
			
			return appColours;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns a page url for an app router
		 *
		 * @returns { String } page url
		 */
		Vue.prototype.$getPageUrl = function(entity, page) {
			return `/${entity.url}/${page.url.replace(/^\/|\/$/, '')}`;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Returns entity homepage or first available page if homepage is not set
		 *
		 * @returns { Object }
		 */
		Vue.prototype.$getEntityHomepage = function(entity) {
			const pages = entity.navigation;
			const homepage = pages.find((page) => page.isHomepage);

			return homepage ? homepage : pages[0];
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Searches for and returns a screen inside the navigation structure
		 *
		 * @param {Array} navigation The navigation array
		 * @param {Object} entity The current entity being traversed
		 * @param {String} screenId The screen id
		 *
		 * @returns {Object}
		 */
		Vue.prototype.$getNavigationScreenById = function(navigation, entity, screenId) {
			let path = undefined;

			if (Array.isArray(navigation)) {
				for (let n = 0, navigationLength = navigation.length; n < navigationLength; n++) {
					let navigationItem = navigation[n];

					if (Array.isArray(navigationItem.subNavigation) && navigationItem.subNavigation.length > 0) {
						path = this.$getNavigationScreenById(navigationItem.subNavigation, entity, screenId);
					}

					if (path) {
						break;
					}

					if (navigationItem.screenId === screenId) {
						path = this.$getPageUrl(entity, navigationItem);
						break;
					}
				}
			}

			return path;
		};

		/**
		 * @public
		 * @global
		 * @description
		 * Searches for and returns a screen inside the navigation structure
		 *
		 * @param {Array} entities The entities array
		 * @param {String} screenId The screen id
		 *
		 * @returns {Mixed}
		 */
		Vue.prototype.$getNavigationEntityById = function(entities, screenId) {
			let routerObject = undefined;

			if (Array.isArray(entities)) {
				for (let i = 0, entitiesLength = entities.length; i < entitiesLength; i++) {
					let entity = entities[i],
						path = undefined;

					if (Array.isArray(entity.navigation) && entity.navigation.length > 0) {
						path = this.$getNavigationScreenById(entity.navigation, entity, screenId);
					}

					if (path != undefined) {
						routerObject = {
							path: path
						};
						break;
					} else {
						if (Array.isArray(entity.subEntities) && entity.subEntities.length > 0) {
							routerObject = this.$getNavigationEntityById(entity.subEntities);
						}
					}
				}
			}

			return routerObject;
		};

		/**
		 * @public
		 * @description
		 * Sets the currently active entity. Used for first load and entity route changes
		 *
		 * @param {Object} entity The entity to store
		 */
		Vue.prototype.$setActiveEntity = function(entity) {
			this.$root.$data.activeEntity = entity;
		};

		/**
		 * @public
		 * @description
		 * Gets the currently active entity. Useful in various locations in the app
		 *
		 * @returns {Object}
		 */
		Vue.prototype.$getActiveEntity = function() {
			return this.$root.$data.activeEntity;
		};

		/**
		 * @public
		 * @description
		 * returns the system wide format for date strings,
		 * retrieved from platform instance settings, or a default
		 *
		 * @param {Boolean} includeTime if true, return the full datetime string
		 *
		 * @returns {String}
		 */
		Vue.prototype.$dateOutputFormat = function(includeTime) {
			return this.$root.$data.platformInstanceSettings.dateDisplayFormat + (includeTime ? ' ' + this.$root.$data.platformInstanceSettings.timeDisplayFormat : '');
		};

		/**
		 * @public
		 * @description
		 * returns the system wide format for time strings
		 *
		 * @returns {String}
		 */
		Vue.prototype.$timeOutputFormat = function() {
			return this.$root.$data.platformInstanceSettings.timeDisplayFormat;
		};

		/**
		 * @public
		 * @description
		 * Return a formatted time string in days, hours minutes and seconds from a moment duration
		 *
		 * @param {Object} duration the moment duration to convert
		 *
		 * @returns {String}
		 */
		Vue.prototype.$formatDuration = function(duration) {
			if (duration.days() >= 1) {
				return Math.floor(duration.asDays()) + 'd ' + Math.floor(duration.asHours()) + 'h ' + Math.floor(duration.minutes()) + 'm ' + Math.floor(duration.seconds()) + 's';
			} else if (duration.asMinutes() >= 60) {
				return Math.floor(duration.asHours()) + 'h ' + Math.floor(duration.minutes()) + 'm ' + Math.floor(duration.seconds()) + 's';
			} else {
				return Math.floor(duration.asMinutes()) + 'm ' + Math.floor(duration.seconds()) + 's';
			}
		};

		/**
		 * @public
		 * @description
		 * Returns the system file name max length, retrieved from platform instance settings
		 *
		 * @returns { Number }
		 */
		Vue.prototype.$getMaxFileNameLength = function() {
			return this.$root.$data.platformInstanceSettings.fileStorageSettings.maxFileNameLength || 100;
		};

		/**
		 * @public
		 * @description
		 * Returns the system file extension whitelist, retrieved from platform instance settings
		 *
		 * @returns { Array }
		 */
		Vue.prototype.$getAllowedFileExtension = function() {
			return this.$root.$data.platformInstanceSettings.fileStorageSettings.fileExtensionWhitelist.map((extension) => extension.toLowerCase());
		};

		/**
		 * @public
		 * @description
		 * Returns the maximum allowed form payload for whole form size validation
		 *
		 * @returns {Number}
		 */
		Vue.prototype.$getMaxAllowedPayload = function() {
			return this.$root.$data.platformInstanceSettings.fileStorageSettings.maxFormPayload * 1000 * 1000;
		};

		/**
		 * @public
		 * @description
		 * Returns the maximum allowed form payload for whole form size validation
		 *
		 * @returns {Number}
		 */
		Vue.prototype.$getHumanMaxAllowedPayload = function() {
			return this.$root.$data.platformInstanceSettings.fileStorageSettings.maxFormPayload + 'MB';
		};

		/**
		 * @public
		 * @description
		 * Returns whether the session diagnostics are currently enabled
		 *
		 * @returns {Boolean}
		 */
		Vue.prototype.$sessionDiagnosticsEnabled = function() {
			return this.$root.$data.platformInstanceSettings && this.$root.$data.platformInstanceSettings.idServerSettings && this.$root.$data.platformInstanceSettings.idServerSettings.sessionDiagnosticsEnabled;
		};

		/**
		 * @public
		 * @description
		 * Shows system popup
		 *
		 * @returns { Object } promise
		 */
		Vue.prototype.$showSystemPopup = function(event, callback) {
			return EventComponentBus.showSystemPopup(event, callback);
		};

		/**
		 * @public
		 * @description
		 * Hides system popup
		 *
		 * @returns void
		 */
		Vue.prototype.$hideSystemPopup = function() {
			return EventComponentBus.hideSystemPopup();
		};

		/**
		 * @public
		 * @description
		 * Checks to see if the browser is capable of inlining an SVG
		 *
		 * @returns {boolean}
		 */
		Vue.prototype.$browserCanInlineSVG = function() {
			return typeof SVGRect != 'undefined';
		};

		/**
		 * @public
		 * @description
		 * Takes the url of a SVG file and loads it dynamically in to the page,
		 * allowing for styling via CSS without messy files
		 *
		 * @param {String} url the url of the SVG to load
		 *
		 * @returns {(Promise|String)}
		 */
		Vue.prototype.$loadSVG = function(url) {
			return new Promise((resolve, reject) => {
				if (typeof SVGRect != 'undefined') {
					this.$axios
						.get(url)
						.then((response) => {
							resolve(response.data);
						})
						.catch((error) => {
							reject(error);
						});
				} else {
					resolve(`<img src="${url}" />`);
				}
			});
		};

		/**
		 * @public
		 * @description
		 * The incoming header logo path and url, then returns a full svg if applicable
		 * or an image string if its not an svg.
		 *
		 * @param {String} imagePath the path of the image
		 * @param {String} imageUrl the full URL to the image
		 * @param {String} imageTitle the title to put on the image
		 * @param {String} headerLogoCsNetworkCallGUID the associated network call guid
		 *
		 * @returns {(Promise|String)}
		 */
		Vue.prototype.$renderConfiguredLogo = async function(imagePath, imageUrl, imageTitle, headerLogoCsNetworkCallGUID = undefined) {
			let returnedImgString = undefined;
			// We're checking if the logo is an svg format
			if (this.$_.endsWith(imagePath, 'svg')) {
				// check to see if we can inline the SVG
				if (this.$browserCanInlineSVG()) {
					try {
						// create a cancellable network request
						headerLogoCsNetworkCallGUID = this.$csNetworkCall.createFetchGetRequest(imageUrl);
						const response = await this.$csNetworkCall.fetchRequest(headerLogoCsNetworkCallGUID)
						// we cancelled it..
						if (response == undefined) {
							return;
						}

						// no data received
						if (response.data == undefined) {
							throw 'No image data received';
						}
						//confirm valid SVG data and extract SVG tag only
						if (typeof response.data !== 'string' || response.data.includes('<svg') === false) {
							throw 'Image data is not a valid SVG file';
						} else if (response.data.substring(0, 4) !== '<svg') {
							response.data = response.data.match(/<svg[^~]*(?=\/svg>).{5}/)[0];
						}
						
						let svgString = response.data;
						svgString = svgString.replace(/<title>[^>]*>/g, '');
						svgString = svgString.replace(/(<svg[^>]*>)/, `$1<title>${imageTitle}</title>`);

						returnedImgString = svgString;
						headerLogoCsNetworkCallGUID = undefined;
						return {returnedImgString, headerLogoCsNetworkCallGUID}
					} catch(error) {
						headerLogoCsNetworkCallGUID = undefined;
						this.$recordError({
							type: 'error',
							error: error,
							displayToUser: true,
							errorCode: '000166'
						});
						return {returnedImgString, headerLogoCsNetworkCallGUID}
					}
				} else {
					returnedImgString = `<img title="${imageTitle}" role="presentation" class="cs-image-header-logo" src="${imageUrl}"/>`;
					return {returnedImgString, headerLogoCsNetworkCallGUID};
				}
			} else {
				returnedImgString = `<img title="${imageTitle}" role="presentation" class="cs-image-header-logo" src="${imageUrl}">`;
				return {returnedImgString, headerLogoCsNetworkCallGUID};
			}
		};

		/**
		 * @public
		 * @description
		 * Returns user accesibility mode value
		 * An accessibilityMode property might be undefined if never been set before
		 * @returns { Boolean }
		 */
		Vue.prototype.$getUserAccessibilityMode = function() {
			return this.$root.$data.userSettings.accessibilityMode;
		};

		/**
		 * @public
		 * @description
		 * Creates and returns an array of action available for the user
		 *
		 * @returns { Array } of action buttons
		 */
		Vue.prototype.$getUserActions = function() {
			const userActions = this.$root.$data.platformInstanceSettings.userActionOptions;
			const userActionsArray = [];

			if (Array.isArray(userActions) && userActions.length > 0) {
				for (let i = 0, action; (action = userActions[i]); i++) {
					if (userActionsArray.indexOf(action.id) === -1) {
						const actionObject = {
							id: action.actionType, //added to help with translations
							actionType: action.actionType,
							text: action.text,
							onText: action.onText,
							offText: action.offText,
							icon: action.icon,
							visible: null
						};
						if (Array.isArray(action.translations)) {
							actionObject.translations = action.translations;
						}
						userActionsArray.push(actionObject);
					}
				}
			}

			// Logout button is hardcoded so we need to add it at the end of the list
			userActionsArray.push({
				id: 'logout',
				actionType: 'logout',
				icon: 'sign-out-alt',
				text: this.$translateWithDefault('signOut'),
				visible: true
			});

			return userActionsArray;
		};

		/**
		 * @public
		 * @description
		 * Returns the current user id
		 *
		 * @returns { String }
		 */
		Vue.prototype.$getUserId = function() {
			return this.$root.$data.userSettings.id;
		};

		/**
		 * @public
		 * @description
		 * Logs out the current user
		 *
		 * @returns {Void}
		 */
		Vue.prototype.$logoutUser = async function() {
			try{
				let appNode = $cs_.getAppNode();
				if (appNode != undefined){
					clearTimeout(appNode.activityData.sessionWarningTimeout);
					clearInterval(appNode.activityData.renewalTimeout);
					if (typeof appNode.activityData.activityWarningResolve === 'function') appNode.activityData.activityWarningResolve('logout-cleanup');
				}	
				EventComponentBus.$off('activityPing');
				localStorage.removeItem('sessionTimeoutWarning');
				localStorage.removeItem('lastActivity');
				localStorage.removeItem('activityTimeoutWarning');
				localStorage.removeItem('sessionStatusId');

				if (!this.user || logoutInProgress === true) return;

				logoutInProgress = true;
				let networkCallGuid = this.$csNetworkCall.createFetchPostRequest(`${this.$getApiBaseUrl()}/api/ActivityLog/UpdateLog`, {userId: this.user.profile.id});
				await this.$csNetworkCall.fetchRequest(networkCallGuid);			
			} catch(error){
				this.$recordError({
					type: 'warning',
					error: error,
					displayToUser: true,
					errorCode: '000382'
				});
			}
			logoutInProgress = false;
			delete this.$axios.defaults.headers.common['Authorization'];
			this.$auth.getUserManager().signoutRedirect();
		};

		/**
		 * @public
		 * @description
		 * Push a value to an array if unique to array
		 *
		 * @returns void
		 */
		Vue.prototype.$pushUniqueValueToArray = function(array, value) {
			if (array.indexOf(value) === -1) {
				array.push(value);
			}
		};

		/**
		 * @public
		 * @description
		 * Removes null, empty and undefined elements from an object
		 *
		 * @returns {object}
		 */
		Vue.prototype.$recursivelyRemoveNullAndEmptyAndUndefinedFromObject = function(object) {
			return $cs_.recursivelyRemoveNullAndEmptyAndUndefinedFromObject(object);
		};

		/**
		 * @public
		 * @description
		 * Checks both variables exist and are the same, ignores case
		 *
		 * @returns {boolean}
		 */
		Vue.prototype.$stringsExistAndMatchesIgnoringCase = function(string1, string2) {
			if (typeof string1 !== 'string' || typeof string2 !== 'string') {
				return false;
			}

			return string1.toLowerCase().trim() === string2.toLowerCase().trim();
		};

		/**
		 * @public
		 * @description
		 * Checks a given object for the existence of a give key, ignoring case
		 *
		 * @param {Object} jsonObject The object to iterate over
		 * @param {String} keyName The key name to search for
		 * @returns {Mixed} This will return undefined if not found or the jsonObject[key] value if found - which could be null
		 */
		Vue.prototype.$getValueByObjectKeyIgnoringCase = function(jsonObject, keyName) {
			return $cs_.getValueByObjectKeyIgnoringCase(jsonObject, keyName);
		};

		/**
		 * @public
		 * @description
		 * Checks a given object for the existence of a give key, ignoring case
		 *
		 * @param {Object} jsonObject The object to iterate over
		 * @param {String} keyName The key name to search for
		 * @returns {Boolean} This will return true or false if object has key
		 */
		Vue.prototype.$objectHasKeyIgnoringCase = function(jsonObject, keyName) {
			let keyNameLowerCase = keyName.toLowerCase(),
				keys = Object.keys(jsonObject);

			if (keys.findIndex((v) => v.toLowerCase() === keyNameLowerCase) !== -1) return true;
			else return false;
		};

		/**
		 * @public
		 * @description
		 * Returns unified string value
		 * If any other type of value, returns value
		 *
		 * @param { Any } value
		 * @returns { String | Any }
		 */
		Vue.prototype.$getValueLowerCase = function(value) {
			return $cs_.getValueLowerCase(value);
		};

		/**
		 * @public
		 * @description
		 * UTF-8 safe base64 encoding
		 *
		 * @param {String} str The string to encode
		 * @returns {string} The encoded string
		 */
		Vue.prototype.$base64EncodeUnicode = function(str) {
			// First we escape the string using encodeURIComponent to get the UTF-8 encoding of the characters,
			// then we convert the percent encodings into raw bytes, and finally feed it to btoa() function.
			const utf8Bytes = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function(match, p1) {
				return String.fromCharCode('0x' + p1);
			});

			return btoa(utf8Bytes);
		};

		Vue.prototype.$injectStylesheetFromObject = function(stylesheetId, cssStylesObject) {
			let injectedSheetElement = document.getElementById(stylesheetId);

			if (injectedSheetElement) {
				for (let i = 0; i < injectedSheetElement.sheet.rules.length; i++) {
					injectedSheetElement.sheet.deleteRule(i);
				}
			} else {
				injectedSheetElement = document.createElement('style');
				injectedSheetElement.setAttribute('id', stylesheetId);

				document.head.appendChild(injectedSheetElement);
			}

			const injectedStyles = injectedSheetElement.sheet;

			for (let i in cssStylesObject) {
				if (cssStylesObject[i]) {
					// add some specificity
					injectedStyles.insertRule(`html body ${i} {${cssStylesObject[i]}}`);
				}
			}
		};

		Vue.prototype.$getRandomInt = function(min, max) {
			min = Math.ceil(min);
			max = Math.floor(max);
			return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
		};

		/**
		 * @description
		 * @public
		 * Handles copy to clipboard event for a passed element
		 *
		 * @returns void
		 */
		Vue.prototype.$copyToClipboard = function(element) {
			if (!element) return;

			// If IE
			if (document.body.createTextRange) {
				const range = document.body.createTextRange();
				range.moveToElementText(element);
				range.select();

				document.execCommand('Copy');
			}
			// Other browsers
			else if (window.getSelection) {
				const selection = window.getSelection();
				const range = document.createRange();

				range.selectNodeContents(element);

				selection.removeAllRanges();
				selection.addRange(range);

				document.execCommand('Copy');
			}
		};

		/**
		 * @public
		 * @description
		 * Checks if browser is IE 11
		 *
		 * @returns { Boolean }
		 */
		Vue.prototype.$browserIsIE11 = function() {
			return !window.ActiveXObject && 'ActiveXObject' in window;
		};

		/**
		 * @public
		 * @description
		 * Checks if browser is Edge
		 *
		 * @returns { Boolean }
		 */
		Vue.prototype.$browserIsEdge = function() {
			return window.navigator.userAgent.indexOf('Edge') > -1;
		};

		/**
		 * @public
		 * @description
		 * Checks if browser is Firefox
		 *
		 * @returns { Boolean }
		 */
		Vue.prototype.$browserIsFF = function() {
			return window.navigator.userAgent.indexOf('Firefox') > -1;
		};

		/**
		 * @public
		 * @description
		 * Removes app loading overlay on index page if visible
		 *
		 * @returns void
		 */
		Vue.prototype.$hideAppLoadingOverlay = function() {
			const csAppLoadinContainer = document.getElementById('cs-app-loading');

			if (csAppLoadinContainer) {
				csAppLoadinContainer.parentNode.removeChild(csAppLoadinContainer);
			}
		};

		/**
		 * @public
		 * @description
		 * Used in vue-multiselect elements to allow IE11 users to click the drop down where there is no text or caret
		 * and have the dropdown open as with all other browsers. Used in multiple places hence it's in the helpers.
		 *
		 * @returns void
		 */
		Vue.prototype.$openVueMultiSelectIfWeAreUsingIE11 = function() {
			if (!window.ActiveXObject && 'ActiveXObject' in window) {
				/* see: https://stackoverflow.com/questions/1553661/how-to-get-the-onclick-calling-object */
				var e = window.event;
				var targ = e.target || e.srcElement;
				if (!e.srcElement.classList.contains('multiselect__select')) {
					if (targ.nodeType == 3) targ = targ.parentNode; // defeat Safari bug
					if (targ.nodeName === 'DIV') {
						var input = targ.querySelector('input');
						if (typeof input !== 'undefined' && input !== null) {
							input.focus();
						}
					}
				}
			}
			/*
				fix for IE11, will cause some issues on IE11 such as not being able to close the
				select field by clicking the open/close button (normally an arrow)
			*/
		};

		Vue.prototype.$fixSVGPreserveAspectRatioForIE11 = function(svgElement) {
			if (svgElement) {
				let currentPreserveAspectRatio = svgElement.getAttribute('preserveAspectRatio');
				if (currentPreserveAspectRatio.indexOf('slice') !== -1) {
					svgElement.setAttribute('preserveAspectRatio', currentPreserveAspectRatio.replace(/\bslice\b/, 'meet'));
				}
			}
		};

		Vue.prototype.$isValidDate = function(dateString) {
			dateString = dateString.slice(0, 10);
			var regEx = /^\d{4}-\d{2}-\d{2}$/;
			if (!dateString.match(regEx)) return false; // Invalid format
			var d = new Date(dateString);
			var dNum = d.getTime();
			if (!dNum && dNum !== 0) return false; // NaN value, Invalid date
			return d.toISOString().slice(0, 10) === dateString;
		};

		/**
		 * @public
		 * @description
		 * Adds the existing field value to an existingComments property and blanks out the value property
		 *
		 * @param object field The field to transform
		 * @returns object
		 */
		Vue.prototype.$handleAppendableCommentsFields = function(field) {
			if (field.definition && field.definition.fieldType === 'cs-comment') {
				if (Array.isArray(field.value)) {
					field.existingComments = field.value.map((fieldValue) => fieldValue);
				} else {
					field.existingComments = [];
				}
				field.value = null;
			}
			return field;
		};

		/**
		 * @public
		 * @description
		 * Returns platform error page settings object
		 *
		 * @returns { Object }
		 */
		Vue.prototype.$getErrorPageSettings = function() {
			return this.$root.$data.platformInstanceSettings.errorPageSettings || {};
		};

		/*
		 * CSS Time to Milliseconds
		 * Thanks to Jake Bellacera (http://jakebellacera.com)
		 * ============================================
		 *
		 * Converts CSS time into milliseconds. Useful for timing functions around CSS animations.
		 * It supports both seconds (s) and milliseconds (ms).
		 *
		 * Arguments:
		 *   time_string - string representation of a CSS time unit (e.g. "1500ms" or "1.5s")
		 *
		 * Returns:
		 *   Time (in milliseconds) as an Integer. Invalid inputs will return 0 milliseconds.
		 *
		 * Example:
		 *   var miliseconds = cssTimeToMilliseconds(myELement.style.transitionDuration);
		 *
		 *   setTimeout(function () {
		 *     alert('Animation complete!');
		 *   }, milliseconds);
		 */
		Vue.prototype.$cssTimeToMilliseconds = (time_string) => {
			var num = parseFloat(time_string, 10),
				unit = time_string.match(/m?s/),
				milliseconds;

			if (unit) {
				unit = unit[0];
			}

			switch (unit) {
				case 's': // seconds
					milliseconds = num * 1000;
					break;
				case 'ms': // milliseconds
					milliseconds = num;
					break;
				default:
					milliseconds = 0;
					break;
			}

			return milliseconds;
		};

		/**
		 * @public
		 * @description
		 * Method to convert a lookup returned object to a string,
		 * uses intelligent defaults and optionally a display template
		 *
		 * @param {Object} inputObject The object containing the keys to use
		 * @param {String} displayTemplate The template to use to generate the returned string
		 */
		Vue.prototype.$convertLookupObjectToString = function(inputObject, displayTemplate = '') {
			let result = undefined;
			if (displayTemplate && displayTemplate.length > 0) {
				result = this.$convertObjectToStringFromTemplate(inputObject, displayTemplate);
			} else if (this.$getValueByObjectKeyIgnoringCase(inputObject, 'displayname')) {
				result = this.$getValueByObjectKeyIgnoringCase(inputObject, 'displayname');
			} else if (this.$getValueByObjectKeyIgnoringCase(inputObject, 'firstname')) {
				result = this.$getValueByObjectKeyIgnoringCase(inputObject, 'firstname') + (this.$getValueByObjectKeyIgnoringCase(inputObject, 'lastname') ? ` ${this.$getValueByObjectKeyIgnoringCase(inputObject, 'lastname')}` : '');
			} else if (this.$getValueByObjectKeyIgnoringCase(inputObject, 'title')) {
				result = this.$getValueByObjectKeyIgnoringCase(inputObject, 'title');
			} else {
				// final fallback, something has gone wrong
				result = inputObject;
			}
			return result;
		};

		/**
		 * Method will meta program a path in string form to extract an object of a string interpoplation
		 * if the final index of the path is an Array it will join the members
		 * @param {Object} source
		 * @param {Array} nextPath
		 * @param {Boolean} isIgnoreCase
		 */
		Vue.prototype.$generateOptionReplaceValue = function(source, nextPath, isIgnoreCase = true) {
			let result = 'Blank';
			if (nextPath.length === 0) {
				if (typeof source === 'string') {
					return source;
				} else if (Array.isArray(source)) {
					return source.join(', ');
				} else {
					return result;
				}
			} else if (nextPath.length === 1) {
				if (Array.isArray(source)) {
					if (source.length > 0) {
						let resultJoin = '';
						let isError = false;
						for (let i = 0, len = source.length; i < len; i++) {
							let option;
							if (isIgnoreCase === true) option = this.$getValueByObjectKeyIgnoringCase(source[i], nextPath[0]);
							else option = source[i][nextPath[0]];
							if (typeof option === 'string') resultJoin = resultJoin + option + ',';
							else isError = true;
						}
						if (isError === false) result = resultJoin.substring(0, resultJoin.length - 1);
					}
				} else if (typeof source === 'object' && source != undefined) {
					if (isIgnoreCase === true) {
						result = this.$getValueByObjectKeyIgnoringCase(source, nextPath[0]);
					} else {
						result = source[nextPath[0]];
					}
				}
				return result;
			} else {
				if (isIgnoreCase === true) {
					source = this.$getValueByObjectKeyIgnoringCase(source, nextPath[0]);
				} else {
					source = source[nextPath[0]];
				}
				if (source == undefined) {
					return result;
				} else {
					nextPath = nextPath.slice(1);
					return this.$generateOptionReplaceValue(source, nextPath, isIgnoreCase); //TCO Tail Optimized recursion
				}
			}
		};

		/**
		 * @public
		 * @description
		 * Takes a display template string and checks to see if we have any lookups/groups.
		 * It will then parse the string against the template and the input object before
		 * returning a converted template string eg {{ SomeLookup[0].title }} etc
		 * @param {Array} templatePieces
		 * @param {String} displayTemplateString
		 * @param {Object} inputObject
		 * 
		 * @return {String}
		*/
		Vue.prototype.$parseAndConvertLookupDisplayTemplateString = function(templatePieces, displayTemplateString, inputObject) {
			for (let templatePieceTargetIndex = templatePieces.length - 1; templatePieceTargetIndex >= 0; templatePieceTargetIndex--) {
				let groupedItems = templatePieces[templatePieceTargetIndex].match(/<<.+?(?=>>)../g);

				//If not undefined we are dealing with a group configuration.
				//Grouped items will be ["<<FirstName>>", "<<LastName>>", "<<Email>>"] in our example
				if (groupedItems != undefined) {
					//remove the external {{ }}'s, then trim so we just have `createdBy<<FirstName>> <<LastName>> (<<Email>>)`
					//We can then find the group key by getting the substring of the start to the first occurence of <<
					const cleanGroupString = templatePieces[templatePieceTargetIndex].replace(/[{{}}]+/g, '').trim();
					const startIndex = cleanGroupString.indexOf('<<');
					const groupKey = cleanGroupString.substr(0, startIndex).trim(); 

					if (inputObject[groupKey]) {
						//replace this particular part of the template with a placeholder we can easily find later
						//which we can re-replace with the new string
						displayTemplateString = displayTemplateString.replace(templatePieces[templatePieceTargetIndex], `CS-REPLACE-ITEM${templatePieceTargetIndex}`);
						
						//now we have a group key we can find this in the input object and find out how many items we have.
						let resultCount = inputObject[groupKey].length;
						
						if (resultCount > 0) {
							let resultIndex = 0;
							const parts = [];
							while (resultIndex < resultCount) {
								// we no longer need the groupKey in our string, we want to isolate the fields within
								// eg <<FirstName>> <<LastName>> (<<Email>>)
								let templateString = cleanGroupString.replace(groupKey, '').trim();
								for (let item of groupedItems) {
									//Get rid of the <<>>'s to just isolate the field name
									const fieldName = item.replace(/[<<>>]+/g, '').trim();

									//Create a compatible string with {{ }}'s that will work in the commonMatches loop below
									const replacement = `{{${groupKey}[${resultIndex}].${fieldName}}}`;

									//now we've made the replacement string, target the orginal template string
									//eg <<FirstName>> <<LastName>> (<<Email>>) which will be replaced with its index also like this:
									//{{createdBy[0].FirstName}} {{createdBy[0].LastName}} ({{createdBy[0].Email}})
									templateString = templateString.replace(`<<${fieldName}>>`, replacement);
								}
								//add this to our parts arrow, so we can join with a comma later
								parts.push(templateString);
								resultIndex++;
							}
							//finally replace the original item in the displayTemplateString
							displayTemplateString = displayTemplateString.replace(`CS-REPLACE-ITEM${templatePieceTargetIndex}`, parts.join(', '));
						}
					}
				}
			}
			
			return displayTemplateString;
		}
		/**
		 * @public
		 * @description
		 * Method to convert an object of keys to an output string
		 * using mustache brackets and a format pattern
		 *
		 * @param {Object} inputObject The object containing the keys to use
		 * @param {String} displayTemplate The template to use to generate the returned string
		 *
		 * @returns {String}
		 */
		Vue.prototype.$convertObjectToStringFromTemplate = function(inputObject, displayTemplate = '') {
			let outputString = displayTemplate;

			if (displayTemplate.length > 0) {
				//We are going to do a primary checkover of the displayTemplate to see if we have configured any groups
				//eg `{{Title}}: {{ createdBy<<FirstName>> <<LastName>> (<<Email>>) }}`
				let templatePieces = displayTemplate.match(/{{.+?(?=}})../g);
				if (templatePieces != undefined) {
					displayTemplate = this.$parseAndConvertLookupDisplayTemplateString(templatePieces, displayTemplate, inputObject);
					
					//now the display template has been modified, reset the outputString to this
					outputString = displayTemplate;
				}

				let commonMatches = displayTemplate.match(/{{.+?(?=}})../g),
					matchedFields = [];

				if (commonMatches != undefined) {
					for (let k = commonMatches.length - 1; k >= 0; k--) {
						let matchPath = commonMatches[k].replace(/[{}]+/g, '').trim();
						matchPath = matchPath.replace('[', '.');
						matchPath = matchPath.replace(']', '');

						matchedFields.push({
							matchedString: commonMatches[k],
							matchedFieldId: matchPath.split('.')
						});
					}
				}

				if (matchedFields.length > 0) {
					for (let matchedField of matchedFields) {
						let replaceValue = this.$generateOptionReplaceValue(inputObject, matchedField.matchedFieldId);
						if (replaceValue != undefined) {
							outputString = outputString.replace(matchedField.matchedString, replaceValue);
						} else {
							outputString = outputString.replace(matchedField.matchedString, ''); //dont show anything - perhaps no s2m or m2m was provided in a group above.
						}
					}
				}
			}

			return outputString;
		};

		/**
		 * @public
		 * @description
		 * If we have configured template parts and are using a lookup template string
		 * (defined by the << >> in the string, then we want to form the below structure
		 * as requested by BE)
		 * 
		 * @param {Object} column
		 * @param {Boolean} includeField false by default, a flag used when field name is required - on sorting and when fetching filter values
		 * @returns String|Undefined
		 */
		Vue.prototype.$makeLookupDisplayFieldTemplateString = (column, includeField = false) => {
			if (column?.displayFieldTemplate != undefined) {
				let outputArray = [column.field]
				let suffix = '_l_'
				let curlyRegex = /({{).*?(}})/; //grabs value between {{ ... }}
				let angledRegex = /(<<).*?(>>)/; //grabs value between << ... >>
				let alphaRegex = /\b\w*\b/; // removes non word characters & whitespace

				let templateCurlyString = column.displayFieldTemplate.match(curlyRegex)[0];
				if (templateCurlyString != undefined) {
					
					let templateCurlyPart = templateCurlyString.match(alphaRegex)[0];
					
					if (includeField || column.isNestedLookup === true) {
						outputArray.push(suffix, templateCurlyPart);
					}
					
					if (includeField 
						&& templateCurlyString.includes('<<')
						&& templateCurlyString.includes('>>')
					) {
						let templateAnglePart = templateCurlyString.match(angledRegex)[0].match(alphaRegex)[0];
						outputArray.push(suffix, templateAnglePart);
					}
					return outputArray.join('');
					}
			}
			return undefined;
		};

		/**
		 * @public
		 * @description
		 * Method to convert a component type string to it's appropriate enum for passing to the API
		 *
		 * @param {String} typeName The string name of the component type
		 *
		 * @returns {Number}
		 */
		Vue.prototype.$getComponentTypeEnum = function(typeName) {
			const enumeration = {
				'grid': 0,
				'landing': 1,
				'reports': 2,
				'userprofile': 3,
				'form': 4,
				'home': 5,
				'config': 6,
				'treeview': 7,
				'accordion': 8
			};

			return enumeration[typeName.toLowerCase()];
		};

		/**
		 * @public
		 * @description
		 * Returns HTML tags whitelist
		 * @param { Array } optionalAdditionalTags
		 *
		 * @returns { Array }
		 */
		Vue.prototype.$getEditorAllowedTags = function(additionalAllowedTags = []) {
			let allowedTags = ['a', 'b', 'blockquote', 'br', 'caption', 'dl', 'dt', 'em', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p', 's', 'strong', 'span', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul', 'video'];
			if (additionalAllowedTags.length > 0) {
				allowedTags = [...allowedTags, ...additionalAllowedTags];
			}
			return allowedTags;
		};

		/**
		 * @public
		 * @description
		 * Returns a list of reserved query params in the app codebase
		 *
		 * @returns { Array }
		 */
		Vue.prototype.$getReservedQueryParams = function() {
			return ['a', 'c', 'r', 't']; // a = an action id, c = create button id, r = rowId, t = tabId
		};

		/**
		 * @public
		 * @description
		 * Allows formatting of numbers across the system
		 */
		Vue.prototype.$numberFormatter = {
			/**
			 * @public
			 * @description
			 * Formats the number for the locale
			 */
			formatter: new Intl.NumberFormat(undefined), // currently this is the system one, later we can add in platform locales etc.
			/**
			 * @public
			 * @description
			 * Formats a number using the formatter, the number formatter is only initialised once per app
			 *
			 * @param number
			 * @returns {string}
			 */
			format: function(number) {
				return this.formatter.format(number);
			}
			// later add in a formatCurrency method to handle currencies when we are using currency codes instead of symbols
			// see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
		};

		/**
		 * Checks to see if a number is a float or not
		 * Borrowed from: https://stackoverflow.com/a/3886106/4939599
		 * Its IE11 safe
		 * @param number
		 * @returns {boolean}
		 */
		Vue.prototype.$isFloat = function(number) {
			return Number(number) === number && number % 1 !== 0;
		};

		/**
		 * @public
		 * @description
		 * Checks if current screen size matches provided breakpoint and returns Boolean
		 *
		 * @param { String } breakpoint
		 * @returns
		 */
		Vue.prototype.$matchMq = function(breakpoint) {
			const breakpoints = Object.keys(this.$root.breakpoints);
			let bI = breakpoints.indexOf(breakpoint.replace(/(\+|-)/g, ''));
			if (bI === -1) return false;

			let currentbI = breakpoints.indexOf(this.$mq);

			if (breakpoint.includes('+') && currentbI >= bI) return true;
			else if (breakpoint.includes('-') && currentbI <= bI) return true;
			else if (currentbI === bI) return true;
			return false;
		};

		/**
		 * Checks if string is valid hex or not
		 * @param string
		 * @returns {boolean}
		 */
		Vue.prototype.$isValidHex = function(value) {
			if (typeof value !== 'string') {
				return false;
			}
			return /^#[0-9A-F]{6}$/i.test(value.trim());
		};

		/**
		 * @public
		 * @description
		 * Removes all special characters from a string
		 *
		 * @param {String} input The input string
		 *
		 * @returns {String}
		 */
		Vue.prototype.$removeNonAlphaNumericCharacters = function(input) {
			if (typeof input !== 'string') {
				return input;
			}
			return input.replace(/[^a-zA-Z0-9]/g, '');
		};

		/**
		 * @public
		 * @description
		 * Removes all html tags from a string
		 *
		 * @param { String } input The input string
		 *
		 * @returns { String }
		 */
		Vue.prototype.$removeHtmlTags = function(input) {
			if (typeof input != 'string') return input;
			input = input.replace(/&nbsp;/g, () => String.fromCharCode(160));
			return input.replace(/(<([^>]+)>)/gi, '');
		};

		/**
		 * @public
		 * @description
		 * Take a file size and make it human readable
		 *
		 * @param { Integer } size size of file in bytes
		 *
		 * @returns { String }
		 */
		Vue.prototype.$readableFileSize = (size) => {
			const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
			let i = 0;
			while(size >= 1024) {
				size /= 1024;
				++i;
			}
			return size.toFixed(1) + ' ' + units[i];
		}

		Vue.prototype.$animateMessage = async (target, animation, maxIterations = 0) => {
			return new Promise((resolve) => {
				target.addEventListener('animationend', animationEndHandler, { once: true });
				target.addEventListener('animationcancel', animationEndHandler, { once: true });
				target.classList.add(animation);

				let iterationCount = 0;

				if ( maxIterations > 0 ) {
					target.addEventListener('animationiteration', animationIterationHandler);
				}

				function animationEndHandler() {
					resolve(target);
					if ( maxIterations > 0 ) target.removeEventListener('animationiteration', animationIterationHandler);
				}

				function animationIterationHandler() {
					iterationCount += 1;
					if ( iterationCount >= maxIterations ) target.classList.remove(animation);
				}
			});
		},
		
		Vue.prototype.$transitionMessage = async (target, styleProps) => {
			return new Promise((resolve) => {
				target.addEventListener('transitionend', transitionEndHandler, { once: true });
				target.addEventListener('transitioncancel', transitionEndHandler, { once: true });

				Object.entries(styleProps).forEach(([prop, value]) => target.style.setProperty(prop, value));

				function transitionEndHandler() {
					resolve(target);
				}
			});
		},

		/**
		 * @public
		 * @description
		 * Check if the value is a valid number whenever is a string or number
		 * If isStrict is false it will check string loosely allowing css values like 10px to pass, otherwise will strictly check for a num
		 * @param { Any } value
		 * @param { Boolean } isStrict tells if should perform a strict or loose check against value, eg. 10px will return false if strict mode.
		 * @returns { Boolean }
		 */
		Vue.prototype.$isNumber = function(value, isStrict = false) {
			if ( typeof value == 'number' ) return true;
			else if ( isStrict === true ) return isFinite(parseFloat(value)) && !isNaN(value);
			else return isFinite(parseFloat(value));
		}

		Vue.prototype.$csVersionString = function() {
			return `${this.$translateWithArgs('versionString', [this.$root.$data.csVersion])}`;
		}

		/**
		 * @public
		 * @description
		 * Removes any duplicates within array of strings separated by commas
		 * @param { Array } array array of strings containing duplicates
		 * @returns { String } Deduplicated string
		 */
		Vue.prototype.$csDeduplicateStringsInArray = function (array) {
			let resultSet = new Set(array.join(', ').split(', ') ?? []);
			return Array.from(resultSet).join(', ');
		}
	}
};
